import { useState } from 'react'

import { ContentModal } from 'components/organisms/modals/content-modal'
import { SignUpForm } from '../../forms/sign-up-form'
import { LoginForm } from '../../forms/login-form'
import { TwoFAVerificationForm } from '../../forms/two-fa-verification-form'
import { AuthResponse } from 'models/response'

type AuthFormType = 'signup' | 'login' | 'verify-otp'

interface AuthModalProps {
	open: boolean
	setOpen: (open: boolean) => void
	initialEmail?: string
	initialForm?: AuthFormType
	inviteId?: string
	redirectTo?: string | null
	onSuccess?: () => any
	hideCloseButton?: boolean
}

export function AuthModal({
	open,
	setOpen,
	initialEmail,
	initialForm = 'signup',
	inviteId,
	redirectTo = null,
	onSuccess,
	hideCloseButton = false,
}: AuthModalProps) {
	const [formType, setFormType] = useState<AuthFormType>(initialForm)
	const [currentEmail, setCurrentEmail] = useState<string | undefined>(initialEmail)
	const handleSuccess = (rs: AuthResponse) => {
		if (!rs.skipMultiFactorAuthentication) {
			setFormType('verify-otp')
		} else {
			closeModal()
			onSuccess?.()
		}
	}

	const handleOtpVerified = () => {
		closeModal()
		onSuccess?.()
	}

	const handleClose = () => {
		closeModal()
	}

	const closeModal = () => {
		setOpen(false)
		// Reset state when modal is closed
		setTimeout(() => {
			setFormType(initialForm)
		}, 300) // Delay to allow modal to close with animation
	}

	const switchToLogin = () => {
		setFormType('login')
	}

	// `email`: "Create an account" after a code proved an address that has no account
	const switchToSignUp = (email?: string) => {
		if (email) setCurrentEmail(email)
		setFormType('signup')
	}

	const handleEmailNotAvailable = (email: string) => {
		setCurrentEmail(email)
		switchToLogin()
	}
	return (
		<ContentModal
			open={open}
			setOpen={handleClose}
			title={formType === 'signup' ? 'Sign up' : formType === 'verify-otp' ? '' : 'Login'}
			customSize="sm:max-w-[500px]"
			isModal={true}
			hideCloseButton={hideCloseButton}
			bodyClassName="sm:!overflow-hidden"
		>
			{formType === 'signup' ? (
				<SignUpForm
					initialEmail={currentEmail || initialEmail}
					onSuccess={handleSuccess}
					isModal={true}
					onSwitchToLogin={switchToLogin}
					onEmailNotAvailable={handleEmailNotAvailable}
					inviteId={inviteId}
				/>
			) : formType === 'verify-otp' ? (
				<TwoFAVerificationForm isModal={true} onVerified={handleOtpVerified} />
			) : (
				<LoginForm
					initialEmail={currentEmail || initialEmail}
					onSuccess={handleSuccess}
					isModal={true}
					onSwitchToSignUp={switchToSignUp}
					inviteId={inviteId}
					redirectTo={redirectTo}
				/>
			)}
		</ContentModal>
	)
}
